import { StatusTagProps } from '../../components/StatusTag/StatusTag';
import { OTP, Subscription } from './collections.domain';
import { toProgramLandingPageUrl } from '@client/helpers/routes';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faEye,
  faEyeSlash,
  faMoneyBillTransfer,
  faUnlock,
} from '@fortawesome/pro-regular-svg-icons';
import { ButtonRecipe } from '@design-system/components/button/button.css';

type SharedProgramRowData = {
  id: string;
  title: string;
  statusTags: StatusTagProps[];
  thumbnailUrl: string;
  rating: string;
  editUrl: string;
};

export type PurchaseRowData = SharedProgramRowData & {
  purchasesCount: string;
  price: string;
  salesPageUrl: string | null;
};

export type SubscriptionRowData = SharedProgramRowData & {
  completions: string;
  comments: string;
  deepLinkUrl: string | null;
  editUrl: string;
};
export type ProgramLists<T> = { drafts: T[]; lives: T[] };

type CollectionPathArgs =
  | {
      type: 'otp';
      collection: Pick<OTP, 'id' | 'name' | 'customURL'>;
    }
  | {
      type: 'subscription';
      collection: Pick<Subscription, 'customURL'>;
    };

export const toCollectionURL = (a: CollectionPathArgs) => {
  if (a.type === 'subscription')
    return `${import.meta.env.VITE_LANDING_PAGE_URL}/${a.collection.customURL}`;

  const programUrl = toProgramLandingPageUrl({
    id: a.collection.id,
    name: a.collection.name ?? '_',
    influencer: { customURL: a.collection.customURL },
  });

  return programUrl;
};

export type SubscriptionEmptyStateOptionsByMode = Array<{
  title: string;
  type: 'platform' | 'ppc';
  details: string;
  tag?: {
    label: string;
    className?: string;
    readonly: boolean;
  };
  list: Array<{ icon: IconProp; title: string; details: string }>;
  cta: Array<
    {
      testId?: string;
      label: string;
      onClick?: () => void;
      to?: string;
      disabled?: boolean;
    } & ButtonRecipe
  >;
}>;

export const toSubscriptionEmptyStateOptionsByMode = ({
  onSelect,
  disabledType,
}: {
  onSelect: (isPPC: boolean) => void;
  disabledType?: 'platform' | 'ppc';
}): SubscriptionEmptyStateOptionsByMode => [
  {
    title: 'Platform Subscription',
    type: 'platform',
    details: 'Share your content with a wider audience',
    list: [
      {
        icon: faMoneyBillTransfer,
        title: 'Pricing',
        details: 'Charge  $14.99/month or $99/year subscription fee.',
      },
      {
        icon: faEye,
        title: 'Who can access your content',
        details:
          'People who discover you on the platform and are Playbook subscribers',
      },
      {
        icon: faUnlock,
        title: 'Discoverability',
        details:
          'Your subscribers can access other platform creators for free.',
      },
    ],
    cta: [
      {
        testId: 'subscriptions-onboarding-pick-type',
        label: 'Select',
        variant: 'filled',
        color: 'neutral',
        onClick: () => onSelect(false),
        disabled: disabledType === 'platform',
      },
      {
        testId: 'subscriptions-onboarding-exclusive',
        label: 'Learn more',
        to: 'https://help.playbookapp.io/en/articles/9698798-how-the-exclusive-subscription-model-works',
      },
    ],
  },
  {
    title: 'Exclusive Subscription',
    type: 'ppc',
    tag: {
      label: 'BETA',
      className: 'bg-[#DA6A02] text-white h-[26px]',
      readonly: true,
    },
    details: 'Gated subscription where you pick your price',
    list: [
      {
        icon: faMoneyBillTransfer,
        title: 'Pricing',
        details: 'You can charge any price you think your content is worth.',
      },
      {
        icon: faEyeSlash,
        title: 'Who can access your content',
        details:
          "Only people with your link and your subscription. You won't be discoverable by others.",
      },
      {
        icon: faUnlock,
        title: 'Discoverability',
        details:
          'Your subscribers cannot access other platform creators for free.',
      },
    ],
    cta: [
      {
        label: disabledType === 'platform' ? 'Select' : 'Request access',
        variant: 'filled',
        color: 'neutral',
        onClick: () => onSelect(true),
        disabled: disabledType === 'ppc',
      },
      {
        label: 'Learn more',
        to: 'https://help.playbookapp.io/en/articles/9698798-how-the-exclusive-subscription-model-works',
      },
    ],
  },
];
